import {
  isSncrNotificacaoTipo,
  isSncrRetencaoTipo,
  SNCR_RETENCAO_CNPJ,
} from "../constants";
import { buildFaixaFromResponse } from "../helpers/numeracaoRange";
import {
  requestNotificacaoReceita,
  requestReceitaEspecialRetencao,
} from "../requests/sncrClient";
import type {
  SncrConselho,
  SncrNotificacaoTipo,
  SncrNotificacaoResponse,
} from "../types";
import {
  getSncrAccessToken,
  isSncrAuthenticated,
} from "./sncrAuth";
import {
  upsertSncrNumeracaoNotificacaoBasic,
} from "./sncrNumeracaoBasicService";

export type SolicitarNotificacaoParams = {
  receita: SncrNotificacaoTipo;
  conselho: SncrConselho;
  uf: string;
  documento: string;
  quantidade?: number;
  practitionerReference?: string;
  organizationReference?: string;
};

export type SolicitarNotificacaoResult = {
  basic: fhir5.Basic;
  faixa: {
    inicio: string;
    fim: string;
    quantidade: number;
    numeros: string[];
  };
  saldoReceitas?: number;
  mensagem?: string;
};

async function fetchNumeracaoApi(
  accessToken: string,
  params: SolicitarNotificacaoParams,
): Promise<SncrNotificacaoResponse> {
  if (isSncrRetencaoTipo(params.receita)) {
    return requestReceitaEspecialRetencao(accessToken, {
      tipo: params.receita,
      conselho: params.conselho,
      uf: params.uf,
      documento: params.documento,
      cnpj: SNCR_RETENCAO_CNPJ,
    });
  }

  if (!isSncrNotificacaoTipo(params.receita)) {
    throw new Error(`Tipo de numeração SNCR inválido: ${params.receita}`);
  }

  if (typeof params.quantidade !== "number") {
    throw new Error("Quantidade é obrigatória para notificação de receita.");
  }

  return requestNotificacaoReceita(accessToken, {
    receita: params.receita,
    conselho: params.conselho,
    uf: params.uf,
    documento: params.documento,
    quantidade: params.quantidade,
  });
}

export async function solicitarNumeracaoNotificacao(
  params: SolicitarNotificacaoParams,
): Promise<SolicitarNotificacaoResult> {
  if (!isSncrAuthenticated()) {
    throw new Error(
      "Sessão SNCR expirada ou inexistente. Conecte ao SNCR (Gov.br) novamente.",
    );
  }

  const accessToken = getSncrAccessToken();
  if (!accessToken) {
    throw new Error("Token SNCR não encontrado. Conecte ao SNCR novamente.");
  }

  const apiResponse = await fetchNumeracaoApi(accessToken, params);
  const faixa = buildFaixaFromResponse(apiResponse);

  const basic = await upsertSncrNumeracaoNotificacaoBasic({
    faixa,
    tipoReceita: params.receita,
    conselho: params.conselho,
    uf: params.uf,
    documento: params.documento,
    saldoReceitas: apiResponse.saldoReceitas,
    mensagemApi: apiResponse.mensagem,
    practitionerReference: params.practitionerReference,
    organizationReference: params.organizationReference,
    status: "disponivel",
  });

  return {
    basic,
    faixa,
    saldoReceitas: apiResponse.saldoReceitas,
    mensagem: apiResponse.mensagem,
  };
}
